import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/common/Layout';
import Loading from '../components/common/Loading';
import { rrhhInventarioService } from '../services/rrhhInventario';
import toast from 'react-hot-toast';
import {
    LayoutDashboard, Shirt, Users, ArrowLeftRight, PackageX, TrendingUp,
    ArrowDownCircle, ArrowUpCircle, ChevronRight, CalendarClock, Inbox, AlertTriangle,
} from 'lucide-react';

const TarjetaKpi = ({ icono: Icono, label, valor, color }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center space-x-3">
        <div className={`p-2.5 rounded-xl ${color}`}>
            <Icono className="h-5 w-5" />
        </div>
        <div>
            <p className="text-xs text-gray-500 uppercase font-semibold">{label}</p>
            <p className="text-xl font-bold text-gray-900">{valor}</p>
        </div>
    </div>
);

const RRHHDashboardPage = () => {
    const navigate = useNavigate();
    const [resumen, setResumen] = useState(null);
    const [movimientos, setMovimientos] = useState([]);
    const [loading, setLoading] = useState(true);

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const [res, movs] = await Promise.all([
                rrhhInventarioService.resumen(),
                rrhhInventarioService.listarMovimientos({ limite: 8 }),
            ]);
            setResumen(res);
            setMovimientos(movs);
        } catch (err) {
            toast.error('Error al cargar el dashboard de RRHH');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { cargar(); }, [cargar]);

    if (loading) {
        return <Layout><div className="p-6"><Loading message="Cargando dashboard..." /></div></Layout>;
    }

    return (
        <Layout>
            <div className="p-6 max-w-6xl mx-auto">
                <div className="flex items-center space-x-3 mb-6">
                    <div className="bg-pink-100 p-2.5 rounded-xl">
                        <LayoutDashboard className="h-6 w-6 text-pink-700" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold text-gray-900">RRHH · Dashboard</h1>
                        <p className="text-sm text-gray-500">Resumen del inventario de uniformes/EPP</p>
                    </div>
                </div>

                {/* Alertas: caducidad de lo entregado y solicitudes de colaboradores */}
                {(resumen.entregas_vencidas > 0 || resumen.entregas_por_vencer > 0 || resumen.solicitudes_pendientes > 0) && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                        <button onClick={() => navigate('/rrhh/caducidades')} className="flex items-center space-x-3 bg-red-50 border border-red-200 rounded-xl p-4 text-left hover:shadow-sm">
                            <AlertTriangle className="h-5 w-5 text-red-600" />
                            <span className="text-sm text-red-800"><strong>{resumen.entregas_vencidas}</strong> entrega(s) de EPP vencida(s)</span>
                        </button>
                        <button onClick={() => navigate('/rrhh/caducidades')} className="flex items-center space-x-3 bg-amber-50 border border-amber-200 rounded-xl p-4 text-left hover:shadow-sm">
                            <CalendarClock className="h-5 w-5 text-amber-600" />
                            <span className="text-sm text-amber-800"><strong>{resumen.entregas_por_vencer}</strong> vence(n) en los próximos 30 días</span>
                        </button>
                        <button onClick={() => navigate('/rrhh/solicitudes')} className="flex items-center space-x-3 bg-teal-50 border border-teal-200 rounded-xl p-4 text-left hover:shadow-sm">
                            <Inbox className="h-5 w-5 text-teal-600" />
                            <span className="text-sm text-teal-800"><strong>{resumen.solicitudes_pendientes}</strong> solicitud(es) por atender</span>
                        </button>
                    </div>
                )}

                {/* KPIs */}
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                    <TarjetaKpi icono={Shirt} label="Productos" valor={resumen.total_productos} color="bg-pink-100 text-pink-700" />
                    <TarjetaKpi icono={Users} label="Empleados" valor={resumen.total_empleados} color="bg-blue-100 text-blue-700" />
                    <TarjetaKpi icono={TrendingUp} label="Unidades en stock" valor={resumen.total_unidades_stock} color="bg-green-100 text-green-700" />
                    <TarjetaKpi icono={PackageX} label="Sin stock" valor={resumen.productos_sin_stock} color="bg-red-100 text-red-700" />
                    <TarjetaKpi icono={ArrowUpCircle} label="Entregas este mes" valor={resumen.entregas_mes_actual} color="bg-indigo-100 text-indigo-700" />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Top stock */}
                    <div className="bg-white rounded-xl border border-gray-200">
                        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                            <h2 className="font-semibold text-gray-800">Productos con más stock</h2>
                            <button onClick={() => navigate('/rrhh/catalogo')} className="text-xs text-blue-600 hover:text-blue-800 flex items-center">
                                Ver catálogo <ChevronRight className="h-3.5 w-3.5" />
                            </button>
                        </div>
                        <div className="divide-y divide-gray-100">
                            {resumen.top_stock.length === 0 && (
                                <p className="px-5 py-6 text-center text-sm text-gray-400">Sin productos</p>
                            )}
                            {resumen.top_stock.map((p) => (
                                <div key={p.sku} className="px-5 py-3 flex items-center justify-between text-sm">
                                    <div>
                                        <p className="font-medium text-gray-900">{p.descripcion}</p>
                                        <p className="text-xs text-gray-400">{p.sku}</p>
                                    </div>
                                    <span className="font-semibold text-gray-700">{p.stock_actual} {p.unidad}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Últimos movimientos */}
                    <div className="bg-white rounded-xl border border-gray-200">
                        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                            <h2 className="font-semibold text-gray-800">Últimos movimientos</h2>
                            <button onClick={() => navigate('/rrhh/movimientos')} className="text-xs text-blue-600 hover:text-blue-800 flex items-center">
                                Ver todos <ChevronRight className="h-3.5 w-3.5" />
                            </button>
                        </div>
                        <div className="divide-y divide-gray-100">
                            {movimientos.length === 0 && (
                                <p className="px-5 py-6 text-center text-sm text-gray-400">Sin movimientos</p>
                            )}
                            {movimientos.map((m) => (
                                <div key={m.id} className="px-5 py-3 flex items-center justify-between text-sm">
                                    <div className="flex items-center space-x-2">
                                        {m.tipo === 'entrada' ? (
                                            <ArrowDownCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                                        ) : (
                                            <ArrowUpCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
                                        )}
                                        <div>
                                            <p className="font-medium text-gray-900">{m.item_descripcion}</p>
                                            <p className="text-xs text-gray-400">
                                                {m.empleado_nombre ? `→ ${m.empleado_nombre}` : 'Reposición'}
                                            </p>
                                        </div>
                                    </div>
                                    <span className="font-semibold text-gray-700">{m.cantidad}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Accesos directos */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
                    <button
                        onClick={() => navigate('/rrhh/catalogo')}
                        className="flex items-center space-x-3 bg-white rounded-xl border border-gray-200 p-4 hover:border-pink-300 hover:shadow-sm transition-all text-left"
                    >
                        <Shirt className="h-5 w-5 text-pink-600" />
                        <span className="font-semibold text-gray-800">Ir a Catálogo</span>
                    </button>
                    <button
                        onClick={() => navigate('/rrhh/movimientos')}
                        className="flex items-center space-x-3 bg-white rounded-xl border border-gray-200 p-4 hover:border-indigo-300 hover:shadow-sm transition-all text-left"
                    >
                        <ArrowLeftRight className="h-5 w-5 text-indigo-600" />
                        <span className="font-semibold text-gray-800">Registrar movimiento</span>
                    </button>
                    <button
                        onClick={() => navigate('/rrhh/empleados')}
                        className="flex items-center space-x-3 bg-white rounded-xl border border-gray-200 p-4 hover:border-blue-300 hover:shadow-sm transition-all text-left"
                    >
                        <Users className="h-5 w-5 text-blue-600" />
                        <span className="font-semibold text-gray-800">Ver empleados</span>
                    </button>
                </div>
            </div>
        </Layout>
    );
};

export default RRHHDashboardPage;
